import React, { useState, useEffect } from 'react';
import { Menu, X, Home, Calendar, User, Phone } from 'lucide-react';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [touchTimeout, setTouchTimeout] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-hide functionality for mobile
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    
    if (!isMobile) return;

    const handleTouchStart = () => {
      setIsVisible(true);
      
      // Clear existing timeout
      if (touchTimeout) {
        clearTimeout(touchTimeout);
      }
      
      // Set new timeout to hide navbar after 3 seconds of no touch
      const timeout = setTimeout(() => {
        if (!isMenuOpen) {
          setIsVisible(false);
        }
      }, 3000);
      
      setTouchTimeout(timeout);
    };

    const handleTouchMove = () => {
      setIsVisible(true);
      
      if (touchTimeout) {
        clearTimeout(touchTimeout);
      }
      
      const timeout = setTimeout(() => {
        if (!isMenuOpen) {
          setIsVisible(false);
        }
      }, 3000);
      
      setTouchTimeout(timeout);
    };

    // Show navbar when menu is opened
    if (isMenuOpen) {
      setIsVisible(true);
      if (touchTimeout) {
        clearTimeout(touchTimeout);
      }
    }

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchmove', handleTouchMove);
    
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      if (touchTimeout) {
        clearTimeout(touchTimeout);
      }
    };
  }, [isMenuOpen, touchTimeout]);

  const menuItems = [
    { href: '#home', label: 'Home', icon: Home },
    { href: '#book-now', label: 'Book Now', icon: Calendar },
    { href: '#aboutus', label: 'About', icon: User },
    { href: '#footer', label: 'Contact', icon: Phone },
  ];

  return (
    <nav
      className={`fixed top-0 w-full z-50 bg-white/95 backdrop-blur-sm px-6 py-4 flex items-center justify-between transition-all duration-500 ${
        scrolled ? 'shadow-lg border-b border-gray-100' : 'shadow-sm'
      } ${
        isVisible ? 'translate-y-0 opacity-100' : 'md:translate-y-0 md:opacity-100 -translate-y-full opacity-0'
      }`}
    >
      {/* Logo */}
      <div className="text-2xl font-bold tracking-tight text-black relative">
        ASHWALAYA
        <div className="absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-black to-gray-800 rounded-full"></div>
      </div>

      {/* Desktop Menu */}
      <ul className="hidden md:flex items-center gap-1">
        {menuItems.map((item, index) => (
          <li key={item.href}>
            <a
              href={item.href}
              className="group relative px-4 py-2 text-sm font-medium text-black hover:text-gray-600 transition-all duration-200 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <item.icon size={16} className="opacity-70 group-hover:opacity-100 transition-opacity" />
              {item.label}
              <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-black transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left"></span>
            </a>
          </li>
        ))}
      </ul>

      {/* Mobile Menu Toggle */}
      <div className="md:hidden">
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <div className={`absolute top-full left-0 w-full bg-white/95 backdrop-blur-sm shadow-lg border-t border-gray-100 transition-all duration-300 ${
        isMenuOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
      } md:hidden`}>
        <div className="px-6 py-4 space-y-1">
          {menuItems.map((item, index) => (
            <a
              key={item.href}
              href={item.href}
              onClick={() => setIsMenuOpen(false)}
              className="group flex items-center gap-3 px-4 py-3 text-sm font-medium text-black hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all duration-200"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <item.icon size={18} className="opacity-70 group-hover:opacity-100 transition-opacity" />
              {item.label}
            </a>
          ))}
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm md:hidden"
          onClick={() => setIsMenuOpen(false)}
          style={{ zIndex: -1 }}
        />
      )}
    </nav>
  );
};

export default Navbar;